import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { Session, TriggeredAlert } from "../../../session";
import { listCoupleSessions, listPatientSessions } from "../../application/sessions";
import { AlertTriangle, Calendar } from "lucide-react";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import Button from "../../../../shared/ui/button/Button";
import DatePicker from "../../../../shared/ui/form/date-picker";
import flatpickr from "flatpickr";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

interface PatientAlertsTabProps {
    patientID: string;
    pageSize?: number;
    hideHeader?: boolean;
    limit?: number;
    onlyCritical?: boolean;
    horizontal?: boolean;
    isCouple?: boolean;
}

export default function PatientAlertsTab({
    patientID,
    pageSize = 5,
    hideHeader = false,
    limit,
    onlyCritical = false,
    horizontal = false,
    isCouple = false,
}: PatientAlertsTabProps) {
    const { t } = useTranslation();
    useBreakpoint();
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [offset, setOffset] = useState(0);
    const [total, setTotal] = useState(0);

    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [criticalityFilter, setCriticalityFilter] = useState<string>("all");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [sortBy, setSortBy] = useState<'date' | 'criticality'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [dateKey, setDateKey] = useState(0);

    const fetchSessions = useCallback(async () => {
        if (!patientID || patientID === 'undefined') return;
        try {
            setLoading(true);
            const response = isCouple
                ? await listCoupleSessions(patientID, { limit: 100 })
                : await listPatientSessions(patientID, { limit: 100 });
            setSessions(response.sessions);
        } catch (err) {
            setError(t("session.errorLoading"));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [patientID, t, isCouple]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const allTriggeredAlerts = useMemo(() => {
        const alerts: (TriggeredAlert & { sessionId: string; sessionTitle: string; sessionDate: string; uniqueId: string })[] = [];
        sessions.forEach((session) => {
            if (session.triggered_alerts) {
                session.triggered_alerts.forEach((alert, idx) => {
                    alerts.push({
                        ...alert,
                        sessionId: session.id,
                        sessionTitle: session.title || t("session.untitled"),
                        sessionDate: session.date || "",
                        uniqueId: `${session.id}-${alert.alert_id}-${idx}`,
                    });
                });
            }
        });
        return alerts;
    }, [sessions, t]);

    const normalizeCriticality = useCallback((c: string): string => {
        const lower = c.toLowerCase();
        if (lower.includes("crítica") || lower.includes("critical")) return "critical";
        if (lower.includes("media") || lower.includes("medium")) return "medium";
        return "low";
    }, []);

    const criticalityHeader = useCallback((c: string): number => {
        const norm = normalizeCriticality(c);
        if (norm === "critical") return 3;
        if (norm === "medium") return 2;
        return 1;
    }, [normalizeCriticality]);

    const filteredAlerts = useMemo(() => {
        let alerts = [...allTriggeredAlerts];

        if (debouncedSearchTerm) {
            const query = debouncedSearchTerm.toLowerCase();
            alerts = alerts.filter(a =>
                a.name.toLowerCase().includes(query) ||
                a.context.toLowerCase().includes(query) ||
                a.sessionTitle.toLowerCase().includes(query)
            );
        }

        if (criticalityFilter !== "all") {
            alerts = alerts.filter(a => normalizeCriticality(a.criticality) === criticalityFilter);
        }

        if (onlyCritical) {
            alerts = alerts.filter(a => normalizeCriticality(a.criticality) === "critical");
        }

        if (startDate) {
            alerts = alerts.filter(a => new Date(a.sessionDate) >= new Date(startDate));
        }

        if (endDate) {
            alerts = alerts.filter(a => new Date(a.sessionDate) <= new Date(endDate));
        }

        // Sorting
        alerts.sort((a, b) => {
            if (sortBy === 'date') {
                const dateA = new Date(a.sessionDate).getTime();
                const dateB = new Date(b.sessionDate).getTime();
                return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            } else {
                const critA = criticalityHeader(a.criticality);
                const critB = criticalityHeader(b.criticality);
                return sortOrder === 'asc' ? critA - critB : critB - critA;
            }
        });

        return alerts;
    }, [allTriggeredAlerts, debouncedSearchTerm, criticalityFilter, onlyCritical, startDate, endDate, sortBy, sortOrder, criticalityHeader, normalizeCriticality]);

    useEffect(() => {
        setTotal(filteredAlerts.length);
    }, [filteredAlerts]);

    const alerts = useMemo(() => {
        const sliced = filteredAlerts.slice(offset, offset + pageSize);
        return limit ? sliced.slice(0, limit) : sliced;
    }, [filteredAlerts, offset, pageSize, limit]);

    const handleNextPage = () => setOffset((prev) => prev + pageSize);
    const handlePrevPage = () => setOffset((prev) => Math.max(0, prev - pageSize));


    const handleFromDateChange = (selectedDates: Date[]) => {
        const formattedDate = selectedDates[0]
            ? flatpickr.formatDate(selectedDates[0], "Y-m-d")
            : "";
        setStartDate(formattedDate);
        setOffset(0);
    };

    const handleToDateChange = (selectedDates: Date[]) => {
        const formattedDate = selectedDates[0]
            ? flatpickr.formatDate(selectedDates[0], "Y-m-d")
            : "";
        setEndDate(formattedDate);
        setOffset(0);
    };

    const getCriticalityBadge = (criticality: string) => {
        const norm = normalizeCriticality(criticality);
        const colors: Record<string, string> = {
            critical: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 border-red-100 dark:border-red-500/20",
            medium: "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400 border-orange-100 dark:border-orange-500/20",
            low: "bg-accent-tint text-accent-text border-accent/20",
        };
        return (
            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider border ${colors[norm]}`}>
                {t(`clinicalAlerts.modal.criticality.${norm}`)}
            </span>
        );
    };

    const clearFilters = () => {
        setSearchTerm("");
        setCriticalityFilter("all");
        setStartDate("");
        setEndDate("");
        setSortBy('date');
        setSortOrder('desc');
        setOffset(0);
        setDateKey(prev => prev + 1);
    };

    if (error) return <p className="text-center text-red-500 py-10">{error}</p>;

    return (
        <div className="space-y-6">
            {/* Filters */}
            {!hideHeader && (
                <div className="flex flex-col xl:flex-row gap-4 items-end bg-gray-50/50 dark:bg-white/[0.02] p-5 rounded-2xl border border-gray-100 dark:border-white/[0.05]">
                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 w-full">
                        {/* Alert Name Search */}
                        <div className="space-y-1.5">
                            <Label htmlFor="search" className="text-xs font-bold text-content-tertiary uppercase tracking-wider">{t("common.search")}</Label>
                            <Input
                                id="search"
                                type="text"
                                placeholder={t("patient.alerts.searchPlaceholder")}
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setOffset(0);
                                }}
                                className="h-11"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-xs font-bold text-content-tertiary uppercase tracking-wider">{t("clinicalAlerts.list.criticality")}</Label>
                            <select
                                value={criticalityFilter}
                                onChange={(e) => {
                                    setCriticalityFilter(e.target.value);
                                    setOffset(0);
                                }}
                                className="w-full h-11 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 focus:border-accent dark:bg-gray-900 dark:border-gray-800 dark:text-white"
                            >
                                <option value="all">{t("common.allStates")}</option>
                                <option value="low">{t("clinicalAlerts.modal.criticality.low")}</option>
                                <option value="medium">{t("clinicalAlerts.modal.criticality.medium")}</option>
                                <option value="critical">{t("clinicalAlerts.modal.criticality.critical")}</option>
                            </select>
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-xs font-bold text-content-tertiary uppercase tracking-wider">{t("common.startDate")}</Label>
                            <DatePicker
                                key={`from-${dateKey}`}
                                id="start-date"
                                label=""
                                placeholder={t("session.selectStartDate")}
                                defaultDate={startDate}
                                onChange={handleFromDateChange}
                            />
                        </div>

                        <div className="space-y-1.5">
                            <Label className="text-xs font-bold text-content-tertiary uppercase tracking-wider">{t("common.endDate")}</Label>
                            <DatePicker
                                key={`to-${dateKey}`}
                                id="end-date"
                                label=""
                                placeholder={t("session.selectEndDate")}
                                defaultDate={endDate}
                                onChange={handleToDateChange}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 w-full xl:w-auto">
                        <Button variant="outline" className="flex-1 xl:flex-none h-11 px-6 whitespace-nowrap" onClick={clearFilters} size="sm">
                            {t("common.clear")}
                        </Button>
                    </div>
                </div>
            )}

            {/* Dashboard Layout */}
            <div className="grid grid-cols-1 gap-4 relative">
                {loading && alerts.length > 0 && (
                    <div className="absolute inset-0 bg-white/50 z-10 dark:bg-gray-900/50 flex justify-center items-center rounded-2xl">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
                    </div>
                )}

                {loading && alerts.length === 0 ? (
                    // SKELETON LOADER
                    Array.from({ length: pageSize }).map((_, i) => (
                        <div key={i} className="p-4 bg-gray-50 dark:bg-white/[0.03] rounded-2xl animate-pulse flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-800"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/3"></div>
                                <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/4"></div>
                            </div>
                        </div>
                    ))
                ) : alerts.length === 0 ? (
                    <div className="p-12 text-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-14 h-14 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-theme-xs">
                                <AlertTriangle className="w-7 h-7 text-content-tertiary" />
                            </div>
                            <p className="text-gray-500 font-medium">{t("patient.noAlertsFound")}</p>
                            <Button variant="outline" size="sm" onClick={clearFilters}>
                                {t("common.clear")}
                            </Button>
                        </div>
                    </div>
                ) : horizontal ? (
                    <div className="space-y-4">
                        {alerts.map((alert) => {
                            const norm = normalizeCriticality(alert.criticality);
                            const style =
                                norm === "critical" ? "border-red-100 bg-red-50/50 text-red-800" :
                                    norm === "medium" ? "border-yellow-100 bg-yellow-50/50 text-yellow-800" :
"border-accent/20 bg-accent-tint text-accent-on-tint";

                            return (
                                <div key={alert.uniqueId} className={`p-4 rounded-xl border ${style} backdrop-blur-md shadow-sm`}>
                                    <p className="text-sm opacity-90 leading-relaxed font-medium">{alert.context}</p>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    alerts.map((row) => (
                        <div
                            key={row.uniqueId}
                            className="group relative flex flex-col sm:flex-row items-center justify-between p-5 bg-white border border-gray-200 rounded-2xl shadow-theme-xs hover:shadow-theme-md transition-all duration-200 dark:bg-gray-900 dark:border-gray-800"
                        >
                            <div className="flex flex-1 items-center gap-5 w-full">
                                {/* Visual Indicator */}
                                <div className={`flex items-center justify-center w-14 h-14 rounded-full shrink-0 ${normalizeCriticality(row.criticality) === "critical"
                                    ? "bg-red-50 text-red-600 dark:bg-red-500/10"
                                    : normalizeCriticality(row.criticality) === "medium"
                                        ? "bg-orange-50 text-orange-600 dark:bg-orange-500/10"
                                        : "bg-accent-tint text-accent-text"
                                    }`}>
                                    <AlertTriangle className="w-7 h-7" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-3 mb-1.5">
                                        <h3 className="text-base font-bold text-content-primary">
                                            {row.name}
                                        </h3>
                                        {getCriticalityBadge(row.criticality)}
                                        <span className="flex items-center gap-1.5 text-sm font-medium text-content-tertiary">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {new Date(row.sessionDate).toLocaleDateString()}
                                        </span>
                                    </div>

                                    <p className="text-sm text-content-tertiary leading-relaxed mb-3">
                                        {row.context}
                                    </p>

                                    <div className="flex items-center gap-4 text-xs font-semibold uppercase tracking-wider">
                                        <span className="text-content-tertiary">{t("session.title")}:</span>
                                        <Link
                                            to={`/session/${row.sessionId}`}
                                            className="text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 transition-colors flex items-center gap-1"
                                        >
                                            {row.sessionTitle}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {!hideHeader && (
                <Pagination
                    offset={offset}
                    total={total}
                    pageSize={pageSize}
                    onPrev={handlePrevPage}
                    onNext={handleNextPage}
                />
            )}
        </div>
    );
}
